import React, { useState, useEffect } from 'react';
import { CheckCircle, Eye } from 'lucide-react';
import InstanceDashboard from './InstanceDashboard';
import { useProcessing } from '../contexts/ProcessingContext';
import { getAuthHeader } from '../services/authToken';

// Generate unique thumbnails for each instance using hash-based selection
const generateThumbnailUrl = (instanceName, instanceId) => {
  if (!instanceName && !instanceId) return 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop';
  
  // Use instance ID or name to generate a consistent hash
  const hashInput = instanceId || instanceName;
  const hash = hashInput.split('').reduce((a, b) => { a = ((a << 5) - a) + b.charCodeAt(0); return a & a; }, 0);
  const imageIndex = Math.abs(hash) % 12;
  
  const thumbnailImages = [
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop', // Books and reading
    'https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=400&h=300&fit=crop', // Mountains landscape
    'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=400&h=300&fit=crop', // Technology workspace
    'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400&h=300&fit=crop', // Business meeting
    'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=400&h=300&fit=crop', // Coding laptop
    'https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?w=400&h=300&fit=crop', // Students studying
    'https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=400&h=300&fit=crop', // Ocean sunset
    'https://images.unsplash.com/photo-1519904981063-b0cf448d479e?w=400&h=300&fit=crop', // Forest path
    'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop', // Mountain peaks
    'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=400&h=300&fit=crop', // Valley landscape
    'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400&h=300&fit=crop', // Meditation sunset
    'https://images.unsplash.com/photo-1493612276216-ee3925520721?w=400&h=300&fit=crop'  // Creative lightbulb
  ];
  
  return thumbnailImages[imageIndex];
};

// Fallback thumbnail with different colors based on instance name hash
const generateFallbackThumbnail = (instanceName) => {
  if (!instanceName) return 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=400&h=300&fit=crop';
  
  // Hash the name to get a consistent color
  const hash = instanceName.split('').reduce((a, b) => { a = ((a << 5) - a) + b.charCodeAt(0); return a & a; }, 0);
  const colorIndex = Math.abs(hash) % 8;
  
  const fallbackImages = [
    'https://images.unsplash.com/photo-1516979187457-637abb4f9353?w=400&h=300&fit=crop', // Books and coffee
    'https://images.unsplash.com/photo-1488190211105-8b0e65b80b4e?w=400&h=300&fit=crop', // Office workspace
    'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=400&h=300&fit=crop', // Working on laptop
    'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=400&h=300&fit=crop', // Clean laptop
    'https://images.unsplash.com/photo-1509062522246-3755977927d7?w=400&h=300&fit=crop', // Teacher classroom
    'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=400&h=300&fit=crop', // Teamwork
    'https://images.unsplash.com/photo-1461360228646-52cf88b34b62?w=400&h=300&fit=crop', // Library shelves
    'https://images.unsplash.com/photo-1529007196863-d07650a3f0ea?w=400&h=300&fit=crop'  // Study desk
  ];
  
  return fallbackImages[colorIndex];
};

const InstanceCard = ({ instance, isSelected, onClick, onUpdate, onDelete, readOnly = false }) => {
  const [showDashboard, setShowDashboard] = useState(false);
  const [realTimeProgress, setRealTimeProgress] = useState(0);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [customThumbnail, setCustomThumbnail] = useState(null);
  const { getJobStatus, getAllJobs } = useProcessing();
  const isReadOnlyMode = readOnly || instance?.is_demo;

  // Safety check - return null if instance is missing or invalid
  if (!instance || !instance.id) {
    console.warn('InstanceCard: Invalid instance object received:', instance);
    return null;
  }

  // Load custom thumbnail - prioritize cover_image from database
  useEffect(() => {
    if (instance?.id) {
      // Priority 1: cover_image from database (persisted across devices)
      if (instance.cover_image) {
        console.log(`✅ Using database cover_image for ${instance.id}`);
        setCustomThumbnail(instance.cover_image);
      } else {
        // Priority 2: localStorage (local cache fallback for legacy data)
        const thumbnailKey = `instance_thumbnail_${instance.id}`;
        const savedThumbnail = localStorage.getItem(thumbnailKey);
        if (savedThumbnail) {
          console.log(`📦 Using localStorage thumbnail for ${instance.id}`);
          setCustomThumbnail(savedThumbnail);
        } else {
          // Priority 3: Generated placeholder
          console.log(`🎨 Using generated placeholder for ${instance.id}`);
          setCustomThumbnail(null); // Will trigger generateThumbnailUrl
        }
      }
    }
  }, [instance?.id, instance?.cover_image]);

  // Use actual progress from backend
  useEffect(() => {
    if (instance.status === 'processing') {
      const jobs = getAllJobs() || [];
      const matchingJob = jobs.find(job => 
        job.instanceId === instance.id || 
        (job.documentId && job.documentId.includes(instance.id))
      );

      if (matchingJob && matchingJob.progress) {
        setProcessingProgress(matchingJob.progress);
      } else {
        // Use the actual progress from the instance API instead of fake time-based progress
        setProcessingProgress(instance.progress || 0);
      }
    }
  }, [instance.status, instance.id, instance.progress, getAllJobs]);

  // Real-time progress updates via custom events (GAP-UI-001 fix)
  useEffect(() => {
    const handleProgressUpdate = (event) => {
      const { instanceId, progress, stage, description } = event.detail;

      // Only update if this is our instance
      if (instanceId === instance.id) {
        console.log(`📊 InstanceCard: Progress update for ${instanceId}:`, { progress, stage, description });
        setProcessingProgress(Math.round(progress || 0));
      }
    };

    // Listen for progress updates dispatched by ProcessingContext
    window.addEventListener('instance-progress-update', handleProgressUpdate);

    // Cleanup listener on unmount to prevent memory leaks
    return () => {
      window.removeEventListener('instance-progress-update', handleProgressUpdate);
    };
  }, [instance.id]); // Only re-subscribe if instance ID changes

  // Fetch reading progress for ready instances
  useEffect(() => {
    const fetchProgress = async () => {
      if (instance?.status === 'ready' && instance?.id) {
        try {
          const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002';
          const response = await fetch(`${backendUrl}/api/progress/instance/${instance.id}`, {
              headers: getAuthHeader()
            });
          if (response.ok) {
            const data = await response.json();
            setRealTimeProgress(data.progress || 0);
          }
        } catch (error) {
          console.error('Error fetching progress:', error);
        }
      }
    };

    fetchProgress();
  }, [instance?.id, instance?.status]);

  const handleThumbnailClick = (e) => {
    if (isReadOnlyMode) {
      return;
    }
    e.stopPropagation(); // Prevent triggering the card click
    setShowDashboard(true);
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick(instance);
    }

    if (isReadOnlyMode) {
      return;
    }

    // Open dashboard for editable instances
    setShowDashboard(true);
  };





  return (
    <>
      <div
        className={`aspect-square bg-gray-100 relative cursor-pointer transition-all rounded-lg overflow-hidden ${
          isSelected 
            ? 'opacity-70 ring-2 ring-blue-500' // Phase 3: Grey + blue ring for active
            : 'hover:opacity-80'
        }`}
        onClick={handleCardClick}
      >
        {/* Thumbnail Image */}
        <div 
          className={`w-full h-full relative ${isSelected ? 'grayscale' : ''}`}
          onClick={handleThumbnailClick}
        >
          <img
            src={customThumbnail || instance?.custom_thumbnail || generateThumbnailUrl(instance?.name, instance?.id)}
            alt={instance?.name || 'Instance'}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = generateFallbackThumbnail(instance?.name);
            }}
          />
          
          {/* Read-only DEMO badge */}
          {isReadOnlyMode && (
            <div
              className="absolute top-2 left-2 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full flex items-center gap-1 font-semibold"
              title="This is a demo instance (read-only)"
              aria-label="Read-only demo instance"
            >
              <Eye className="w-3 h-3" />
              DEMO
            </div>
          )}

          {/* Phase 3: Blue checkmark for active instance (only for non-demo) */}
          {isSelected && !isReadOnlyMode && (
            <div className="absolute top-2 left-2 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center">
              <CheckCircle className="w-4 h-4" />
            </div>
          )}

          {/* Status indicator (only for non-active, non-demo instances) */}
          {!isSelected && !isReadOnlyMode && (
            <div className={`absolute top-2 left-2 w-3 h-3 rounded-full ${
              instance.status === 'ready' ? 'bg-green-500' :
              instance.status === 'processing' ? 'bg-yellow-500 animate-pulse' :
              instance.status === 'failed' ? 'bg-red-500' :
              'bg-gray-400'
            }`} />
          )}

          {/* Processing Progress Overlay - Clean and stable version */}
          {instance.status === 'processing' && (() => {
            // Determine color based on progress percentage (GAP-UI-001 stage-based colors)
            const getProgressColor = (progress) => {
              if (progress >= 90) return '#22c55e'; // Green - finalization
              if (progress >= 75) return '#6366f1'; // Indigo - entity extraction
              if (progress >= 60) return '#a855f7'; // Purple - image generation
              if (progress >= 20) return '#f97316'; // Orange - AI processing
              return '#fbbf24'; // Yellow - parsing
            };

            return (
              <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex flex-col justify-between p-3">

                {/* Top Section - Instance Title */}
                <div className="flex-shrink-0">
                  <h3 className="text-white text-sm font-semibold line-clamp-2 text-center drop-shadow-lg">
                    {instance.name || 'Untitled Instance'}
                  </h3>
                </div>

                {/* Center Section - Progress Ring */}
                <div className="flex-1 flex items-center justify-center">
                  <div
                    className="relative w-16 h-16"
                    data-testid="instance-progress-ring"
                    data-progress={processingProgress}
                    data-instance-id={instance.id}
                  >
                    <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 64 64">
                      {/* Background Circle */}
                      <circle
                        cx="32"
                        cy="32"
                        r="28"
                        stroke="rgba(255, 255, 255, 0.15)"
                        strokeWidth="2.5"
                        fill="transparent"
                      />
                      {/* Progress Circle - Dynamic color based on stage */}
                      <circle
                        cx="32"
                        cy="32"
                        r="28"
                        stroke={getProgressColor(processingProgress)}
                        strokeWidth="2.5"
                        fill="transparent"
                        strokeDasharray={`${2 * Math.PI * 28}`}
                        strokeDashoffset={`${2 * Math.PI * 28 * (1 - processingProgress / 100)}`}
                        className="transition-all duration-300 ease-out"
                        strokeLinecap="round"
                      />
                    </svg>
                    {/* Centered Percentage Text */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span
                        className="text-white text-base font-bold drop-shadow-lg"
                        data-testid="instance-progress-percentage"
                      >
                        {processingProgress}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Bottom Section - Status and Post Count */}
                <div className="flex-shrink-0 text-center">
                  <div className="text-white text-xs font-medium drop-shadow-lg mb-1">
                    <span
                      className="animate-pulse"
                      data-testid="instance-progress-status"
                    >
                      Processing...
                    </span>
                  </div>
                  <div className="text-white/80 text-xs">
                    {instance.posts_count || 0} posts
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Reading Progress Overlay - Show for instances with progress */}
          {instance.status === 'ready' && (
            (realTimeProgress > 0 && realTimeProgress < 100) ? (
              <div className="absolute top-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs transition-all duration-300 ease-in-out">
                {Math.round(realTimeProgress)}% read
              </div>
            ) : (instance.progress > 0 && instance.progress < 100) ? (
              <div className="absolute top-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs transition-all duration-300 ease-in-out">
                {instance.progress}% read
              </div>
            ) : null
          )}

          {/* Processing Progress Bar (fallback for instances without specific progress) */}
          {instance.status === 'processing' && instance.progress === undefined && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/30 transition-all duration-300 ease-in-out">
              <div className="h-full bg-yellow-500 animate-pulse transition-all duration-500 ease-in-out" />
            </div>
          )}


          
          {/* Info overlay - Only show when NOT processing to avoid duplication */}
          {instance.status !== 'processing' && (
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent backdrop-blur-md p-2">
                <p className="text-white text-xs font-semibold line-clamp-2 drop-shadow-lg">
                  {instance.name}
                </p>
                <div className="flex justify-between text-white/90 text-xs mt-1">
                  <span>{instance.posts_count || 0} posts</span>
                  {instance.status === 'ready' ? (
                    realTimeProgress > 0 ? (
                      <span className="text-blue-300">{Math.round(realTimeProgress)}% read</span>
                    ) : instance.progress > 0 ? (
                      <span className="text-blue-300">{instance.progress}% read</span>
                    ) : (
                      <span className="text-green-300">Ready</span>
                    )
                  ) : instance.status === 'failed' ? (
                    <span className="text-red-300">Failed</span>
                  ) : (
                    <span className="text-gray-300">-</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Instance Dashboard */}
      {showDashboard && (
        <InstanceDashboard
          instance={instance}
          readOnly={isReadOnlyMode}
          onClose={() => setShowDashboard(false)}
        />
      )}

    </>
  );
};

export default InstanceCard;
